/**
 * E002-T018: Input Tab Component
 * E002-T044: System-Filter Display
 * E002-T047: Batch Size Slider
 * E007-T022: Schema-based Input Mapping
 * E029-S020-8.20.1: Auto-Join Relations (Multi-Input)
 *
 * Displays source table selection, filters (using Data-View FilterGroup),
 * column selection or schema-based input mapping, and batch size configuration.
 * Uses the native Data-View filter UI for consistent UX.
 *
 * When a workflow with schema is selected, shows InputMappingGrid instead of ColumnSelector.
 * When status tracking is enabled, shows a locked system filter
 * that automatically excludes already processed rows.
 * Lead-Cluster UX: Lead table + explicitly added Related Tables (via relation columns)
 * are grouped together, separated from Free Tables below.
 */

import { Menu, MenuItem, notify } from '@affine/component';
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { useFeatureGate } from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { useI18n } from '@affine/i18n';
import {
  type InputTable,
  MAX_RELATION_TABLES_DISPLAY,
} from '@blocksuite/affine/blocks/streamlain';
import type { FilterGroup } from '@blocksuite/affine/data-view';
import { cssVarV2 } from '@streamlain/theme/v2';
import { useLiveData, useService } from '@toeverything/infra';
import type { ChangeEvent, FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { WorkspaceService } from '../../workspace';
import { useAiKeyCheck } from '../hooks/use-ai-key-check';
import { FEATURE_KEY_WORKFLOW } from '../hooks/use-feature-gate';
import type { AutomationConfig, InputMapping } from '../services/automation';
import { DEFAULT_STATUS_TRACKING_CONFIG } from '../services/automation';
import { AutomationConfigApiService } from '../services/automation-config-api';
import type { ColumnConflict } from '../services/automation-discovery';
import type { ColumnInfo, DatabaseInfo } from '../services/database-discovery';
import { WorkflowService } from '../services/workflow';
import { TutorialRegistry } from '../tutorial/services/tutorial-registry';
import type { CronPreset, TriggerConfig, TriggerType } from '../types';
import { CRON_PRESETS, DEFAULT_TRIGGER_CONFIG } from '../types';
import * as styles from './automation-panel.css';
import { CollapsibleSection } from './components/collapsible-section';
import { ColumnSelector } from './components/column-selector';
import { InputMappingGrid } from './components/input-mapping-grid';
import { NativeFilterPanel } from './components/native-filter-panel';
import {
  SectionAddLink,
  SectionControlRow,
  SectionDivider,
  SectionNotice,
  SectionPanelHeader,
  SectionRemoveButton,
  SectionStack,
  SectionTableRow,
} from './components/section-layout';
import { StatusTrackingSection } from './components/status-tracking-section';
import { TableSelector } from './components/table-selector';

// sl-i6t9 (A4): UI display cap imported from shared types.
// Backend enforces its own per-row link limit (DEFAULT_RELATION_LINK_LIMIT = 100).
// Mirrored from backend AUTOMATION_BATCH_ROW_LIMIT. Backend remains authoritative.
const ROWS_PER_CALL_MIN = 1;
const ROWS_PER_CALL_DEFAULT = 5;
const ROWS_PER_CALL_MAX = 100;

const DEFAULT_BATCH_RETRY = {
  enabled: false,
  maxAttempts: 3,
  initialDelayMs: 1000,
  backoffMultiplier: 2,
  maxDelayMs: 30000,
};

function clampRowsPerCall(value: number, fallback = ROWS_PER_CALL_DEFAULT) {
  const safeValue = Number.isFinite(value) ? Math.floor(value) : fallback;
  return Math.max(ROWS_PER_CALL_MIN, Math.min(ROWS_PER_CALL_MAX, safeValue));
}

interface InputTabProps {
  config: AutomationConfig;
  onUpdate: (updates: Partial<AutomationConfig>) => void;
  /** Source table columns (for resolving column names) */
  sourceColumns?: ColumnInfo[];
  /** E029-S020-8.20.1: All accessible databases (for relation lookup) */
  accessibleDatabases?: DatabaseInfo[];
  /** S020-8.20.6: Map of column IDs to conflict info (for status tracking) */
  usedColumns?: Map<string, ColumnConflict>;
  /** S020-8.20.6: Callback to navigate to conflicting automation */
  onNavigateToConflict?: (automationId: string, docId: string) => void;
  /** Trigger: schedule toggle callback */
  onScheduleToggle?: (enabled: boolean) => Promise<boolean>;
  /** Trigger: schedule saving state */
  isScheduleSaving?: boolean;
  /** Block ID for webhook + AI key check */
  blockId?: string;
  /** sl-1ahs.4.1: Highlight the status tracking section when enable-toggle fails */
  onHighlightStatusTracking?: () => void;
  /** Create a status Select column in the primary input table */
  onCreateStatusColumn?: () => Promise<void> | void;
}

export const InputTab: FC<InputTabProps> = ({
  config,
  onUpdate,
  sourceColumns,
  accessibleDatabases,
  usedColumns,
  onNavigateToConflict,
  onScheduleToggle,
  isScheduleSaving = false,
  blockId,
  onHighlightStatusTracking,
  onCreateStatusColumn,
}) => {
  const t = useI18n();
  const registry = useService(TutorialRegistry);
  const workspace = useService(WorkspaceService).workspace;
  const configApiService = useService(AutomationConfigApiService);
  const { isFeatureAllowed } = useFeatureGate();
  const hasWorkflowFeature = isFeatureAllowed(FEATURE_KEY_WORKFLOW);
  const missingAiKeys = useAiKeyCheck(
    configApiService,
    blockId,
    workspace?.id,
    config.workflowId
  );

  // Workspace webhooks for lead-source dropdown + URL display in AUSLÖSER (RBAC-filtered)
  const [workspaceWebhooks, setWorkspaceWebhooks] = useState<
    { credentialId: string; name: string; webhookUrl: string }[]
  >([]);
  useEffect(() => {
    if (config.triggerConfig?.type !== 'webhook' || !workspace?.id) return;
    if (!hasWorkflowFeature) return;
    workspaceApiFetch(
      `/api/workspace/${workspace.id}/inbound-webhooks`,
      {
        credentials: 'include',
      },
      {
        workspaceId: workspace.id,
        category: 'workspace-api',
        key: 'inbound-webhooks',
        mode: 'background',
        priority: 'background',
      }
    )
      .then(res => (res.ok ? res.json() : []))
      .then(
        (
          data: {
            credentialId: string;
            name: string;
            webhookUrl: string;
          }[]
        ) => {
          setWorkspaceWebhooks(
            (data ?? []).map(w => ({
              credentialId: w.credentialId,
              name: w.name,
              webhookUrl: w.webhookUrl,
            }))
          );
        }
      )
      .catch(() => {});
  }, [config.triggerConfig?.type, workspace?.id, hasWorkflowFeature]);

  // E007-T022: Get workflow service for schema
  // E018-S005-T001: Schema is now loaded in ProcessTab when workflow is selected
  const workflowService = useService(WorkflowService);
  const selectedSchema = useLiveData(workflowService.selectedWorkflowSchema$);
  const schemaLoadingMap = useLiveData(workflowService.schemaLoading$);

  // sl-za0v.13.2: Schema loading state for the current workflow
  // Default to true (assume loading) until explicitly set to false by loadWorkflowSchema.
  // Prevents flash of "schema load failed" before the first fetch completes.
  const isSchemaLoading = config.workflowId
    ? (schemaLoadingMap.get(config.workflowId) ?? true)
    : false;

  // Check if we have a schema with input fields
  const hasSchema = !!(
    selectedSchema?.input?.required?.length ||
    selectedSchema?.input?.optional?.length
  );

  // sl-za0v.13.2: Schema failed to load (workflow selected, not loading, no schema)
  const schemaLoadFailed =
    !!config.workflowId && !isSchemaLoading && !hasSchema && !selectedSchema;

  // ── Trigger config ──
  const triggerConfig = config.triggerConfig ?? DEFAULT_TRIGGER_CONFIG;
  const hasSelectedWebhookSource =
    triggerConfig.type === 'webhook' && !!triggerConfig.webhookCredentialId;

  const handleTriggerConfigChange = useCallback(
    (updates: Partial<TriggerConfig>) => {
      onUpdate({ triggerConfig: { ...triggerConfig, ...updates } });
    },
    [onUpdate, triggerConfig]
  );

  const handleTriggerTypeChange = useCallback(
    (newType: TriggerType) => {
      handleTriggerConfigChange({ type: newType, enabled: false });
    },
    [handleTriggerConfigChange]
  );

  const handleCronPresetChange = useCallback(
    (preset: CronPreset) => {
      handleTriggerConfigChange({ cronPreset: preset, enabled: false });
    },
    [handleTriggerConfigChange]
  );

  const handleTriggerEnabledToggle = useCallback(async () => {
    const newEnabled = !triggerConfig.enabled;

    if (newEnabled && missingAiKeys.length > 0) {
      notify({
        title:
          t['com.streamlain.streamlain.validation.cannot-activate-trigger'](),
        message: t['com.streamlain.streamlain.process.missing-ai-keys-detail']({
          keys: missingAiKeys.join(', '),
        }),
        style: 'normal',
        theme: 'error',
      });
      return;
    }

    if (newEnabled) {
      if (triggerConfig.type === 'webhook' && !hasSelectedWebhookSource) {
        notify({
          title:
            t['com.streamlain.streamlain.validation.cannot-activate-trigger'](),
          message:
            t[
              'com.streamlain.streamlain.validation.webhook-source-required'
            ]?.() ?? 'Please select a webhook source first.',
          style: 'normal',
          theme: 'error',
        });
        return;
      }

      const statusTracking = config.statusTracking;
      if (!statusTracking?.enabled) {
        notify({
          title:
            t['com.streamlain.streamlain.validation.cannot-activate-trigger'](),
          message:
            t[
              'com.streamlain.streamlain.validation.status-tracking-required'
            ](),
          style: 'normal',
          theme: 'error',
        });
        // sl-1ahs.4.1: Also jump to status tracking section so user sees where to fix it
        onHighlightStatusTracking?.();
        return;
      }
      if (!statusTracking.columnId) {
        notify({
          title:
            t['com.streamlain.streamlain.validation.cannot-activate-trigger'](),
          message:
            t[
              'com.streamlain.streamlain.validation.status-tracking-incomplete'
            ](),
          style: 'normal',
          theme: 'error',
        });
        // sl-1ahs.4.1: Also jump to status tracking section so user sees where to fix it
        onHighlightStatusTracking?.();
        return;
      }

      const { sourceTableId, outputTables, matchConfigs } = config;
      if (sourceTableId && outputTables?.length) {
        const inputEqualsOutput = outputTables.some(
          ot => ot.tableId === sourceTableId
        );
        if (inputEqualsOutput) {
          const matchTable = outputTables.find(
            ot => ot.tableId === sourceTableId
          );
          const rowMc = matchTable?.matchConfigId
            ? (matchConfigs ?? []).find(
                mc => mc.id === matchTable.matchConfigId
              )
            : undefined;
          if (!rowMc || rowMc.keys.length === 0) {
            notify({
              title:
                t[
                  'com.streamlain.streamlain.validation.cannot-activate-trigger'
                ](),
              message:
                t['com.streamlain.streamlain.validation.match-key-required'](),
              style: 'normal',
              theme: 'error',
            });
            return;
          }
        }
      }
    }

    if (onScheduleToggle) {
      const success = await onScheduleToggle(newEnabled);
      if (success) {
        handleTriggerConfigChange({ enabled: newEnabled });
        const notification = {
          title: newEnabled
            ? t['com.streamlain.streamlain.process.schedule-active-info']()
            : t['com.streamlain.streamlain.process.schedule-inactive-info'](),
        };
        if (newEnabled) {
          notify.success(notification);
        } else {
          notify.warning(notification);
        }
      }
    } else {
      handleTriggerConfigChange({ enabled: newEnabled });
      const notification = {
        title: newEnabled
          ? t['com.streamlain.streamlain.process.schedule-active-info']()
          : t['com.streamlain.streamlain.process.schedule-inactive-info'](),
      };
      if (newEnabled) {
        notify.success(notification);
      } else {
        notify.warning(notification);
      }
    }
  }, [
    handleTriggerConfigChange,
    triggerConfig.enabled,
    triggerConfig.type,
    onScheduleToggle,
    config,
    t,
    missingAiKeys,
    hasSelectedWebhookSource,
    onHighlightStatusTracking,
  ]);

  useEffect(() => {
    if (hasWorkflowFeature || triggerConfig.type !== 'webhook') return;
    onUpdate({
      triggerConfig: {
        ...triggerConfig,
        type: 'manual',
        enabled: false,
        webhookCredentialId: null,
      },
    });
  }, [hasWorkflowFeature, onUpdate, triggerConfig]);

  const triggerTypeOptions: { value: TriggerType; label: string }[] = [
    {
      value: 'manual',
      label: t['com.streamlain.streamlain.process.trigger-manual'](),
    },
    {
      value: 'scheduled',
      label: t['com.streamlain.streamlain.process.trigger-scheduled'](),
    },
    ...(hasWorkflowFeature
      ? [
          {
            value: 'webhook' as TriggerType,
            label:
              t['com.streamlain.streamlain.process.schedule.webhook']?.() ??
              'Webhook',
          },
        ]
      : []),
  ];

  const cronI18n: Record<string, string> = {
    every_1_min: t['com.streamlain.streamlain.process.cron.every-1-min'](),
    every_5_min: t['com.streamlain.streamlain.process.cron.every-5-min'](),
    every_15_min: t['com.streamlain.streamlain.process.cron.every-15-min'](),
    every_30_min: t['com.streamlain.streamlain.process.cron.every-30-min'](),
    hourly: t['com.streamlain.streamlain.process.cron.hourly'](),
    daily_9am: t['com.streamlain.streamlain.process.cron.daily-9am'](),
    daily_6pm: t['com.streamlain.streamlain.process.cron.daily-6pm'](),
    weekly_monday_9am:
      t['com.streamlain.streamlain.process.cron.weekly-monday'](),
    monthly_first_9am:
      t['com.streamlain.streamlain.process.cron.monthly-first'](),
  };

  // Check if status tracking is enabled for system filter display
  const statusTracking =
    config.statusTracking ?? DEFAULT_STATUS_TRACKING_CONFIG;
  const hasSystemFilter = statusTracking.enabled && !!statusTracking.columnId;

  // Get column name for system filter display
  const systemFilterColumnName = useMemo(() => {
    if (!hasSystemFilter || !sourceColumns) return null;
    const col = sourceColumns.find(c => c.id === statusTracking.columnId);
    return col?.name || 'Status';
  }, [hasSystemFilter, sourceColumns, statusTracking.columnId]);

  const primarySourceColumns = useMemo(() => {
    const primaryDb = accessibleDatabases?.find(
      db => db.blockId === config.sourceTableId
    );
    return primaryDb?.columns ?? sourceColumns ?? [];
  }, [accessibleDatabases, config.sourceTableId, sourceColumns]);

  const handleSourceTableChange = (database: DatabaseInfo) => {
    onUpdate({
      sourceTableId: database.blockId,
      sourceTableName: database.databaseTitle,
      sourceDocId: database.docId, // E018-S008-T005: Store docId for CrossDoc indicator
      triggerConfig: {
        ...triggerConfig,
        webhookCredentialId: null,
      },
      // Reset all table-dependent config when source table changes
      selectedColumns: [],
      filterGroup: undefined, // Reset filters
      inputTables: [], // sl-m379: Reset secondary tables (auto-detected will regenerate)
      // sl-za0v.13.1: Reset batch size on table change (was missing — orphaned settings bug)
      ...(config.batchConfig
        ? {
            batchConfig: {
              ...config.batchConfig,
              batchSize: 1,
              executionMode: 'row',
            },
          }
        : {}),
      // Disable status tracking (column IDs are source-table-specific)
      statusTracking: {
        ...statusTracking,
        enabled: false,
        columnId: null,
      },
      // NOTE: outputMappings are NOT reset here — they map workflow response
      // fields to TARGET table columns, not source table columns.
    });
  };

  // sl-za0v.12.3: handleExternalSelect removed — webhook mode is set via trigger type in Process Tab

  // Filter handler - direct update from NativeFilterPanel (lead table)
  const handleFilterChange = useCallback(
    (newFilterGroup: FilterGroup) => {
      onUpdate({ filterGroup: newFilterGroup });
    },
    [onUpdate]
  );

  // sl-m379: Filter handler for secondary tables
  const handleSecondaryFilterChange = useCallback(
    (tableId: string, newFilterGroup: FilterGroup) => {
      const current = config.inputTables ?? [];
      const updated = current.map(t =>
        t.tableId === tableId ? { ...t, filterGroup: newFilterGroup } : t
      );
      onUpdate({ inputTables: updated });
    },
    [config.inputTables, onUpdate]
  );

  // Webhook credential selection handler
  const handleWebhookCredentialSelect = useCallback(
    (credentialId: string | null) => {
      const manualSecondaryTables = (config.inputTables ?? []).filter(
        table => table.role === 'secondary' && !table.linkedVia
      );
      onUpdate({
        sourceTableId: null,
        sourceTableName: null,
        sourceDocId: null,
        selectedColumns: [],
        filterGroup: undefined,
        inputTables: manualSecondaryTables,
        statusTracking: {
          ...statusTracking,
          enabled: false,
          columnId: null,
        },
      });
      handleTriggerConfigChange({ webhookCredentialId: credentialId });
    },
    [config.inputTables, handleTriggerConfigChange, onUpdate, statusTracking]
  );

  // Column selection handler (legacy mode without schema)
  // E018-S002-T009: Clear inputMappings when using legacy mode
  const handleColumnSelectionChange = useCallback(
    (selectedColumns: string[]) => {
      onUpdate({ selectedColumns, inputMappings: [] });
    },
    [onUpdate]
  );

  // E007-T022: Input mapping handler (schema mode)
  // E018-S002-T009: Only set inputMappings, clear selectedColumns to avoid redundancy
  const handleInputMappingsChange = useCallback(
    (inputMappings: InputMapping[]) => {
      // Clear selectedColumns when using schema-based mapping (E018-S002-T009)
      onUpdate({ inputMappings, selectedColumns: [] });
    },
    [onUpdate]
  );

  // Available relation columns from lead table (for "+ Add Related Table" dropdown)
  const availableRelations = useMemo(() => {
    if (!sourceColumns || !accessibleDatabases) return [];
    const alreadyLinked = new Set(
      (config.inputTables ?? []).flatMap(t =>
        t.linkedVia ? [t.linkedVia] : []
      )
    );
    return sourceColumns
      .flatMap(col => {
        if (
          col.type !== 'relation' ||
          !col.targetDatabaseId ||
          alreadyLinked.has(col.id)
        ) {
          return [];
        }
        const targetDb = accessibleDatabases.find(
          db => db.blockId === col.targetDatabaseId
        );
        return [
          {
            columnId: col.id,
            columnName: col.name,
            targetTableId: col.targetDatabaseId,
            targetTableName: targetDb?.databaseTitle ?? 'Unknown',
            targetDocId: col.targetDocId ?? targetDb?.docId,
            cardinality: col.cardinality ?? ('one' as const),
          },
        ];
      })
      .slice(0, MAX_RELATION_TABLES_DISPLAY);
  }, [sourceColumns, accessibleDatabases, config.inputTables]);

  // Add a related table via relation column
  const handleAddRelatedTable = useCallback(
    (relation: (typeof availableRelations)[number]) => {
      const current = config.inputTables ?? [];
      const newTable: InputTable = {
        id: `linked-${relation.columnId}`,
        tableId: relation.targetTableId,
        tableName: relation.targetTableName,
        docId: relation.targetDocId,
        role: 'secondary',
        linkedVia: relation.columnId,
        cardinality: relation.cardinality,
      };
      onUpdate({ inputTables: [...current, newTable] });
    },
    [config.inputTables, onUpdate]
  );

  // Build effective input tables list: lead + related (linkedVia) + free (manual)
  const effectiveInputTables = useMemo((): InputTable[] => {
    const allSecondaries = (config.inputTables ?? []).filter(
      t => t.role === 'secondary'
    );
    const lead: InputTable = {
      id: 'lead',
      tableId: config.sourceTableId ?? '',
      tableName: config.sourceTableName ?? '',
      docId: config.sourceDocId ?? undefined,
      role: 'lead',
      filterGroup: config.filterGroup,
    };
    return [lead, ...allSecondaries];
  }, [
    config.sourceTableId,
    config.sourceTableName,
    config.sourceDocId,
    config.filterGroup,
    config.inputTables,
  ]);

  // sl-m379: Build column groups for InputMappingGrid
  const secondaryColumnGroups = useMemo(() => {
    if (!accessibleDatabases) return [];
    const secondaries = effectiveInputTables.filter(
      t => t.role === 'secondary'
    );
    return secondaries
      .map(t => {
        const db = accessibleDatabases.find(d => d.blockId === t.tableId);
        return {
          inputTableId: t.id,
          tableId: t.tableId,
          label: t.tableName,
          columns: db?.columns ?? [],
          // sl-i6t9: Pass cardinality for visual indicator in mapping grid
          cardinality: t.cardinality,
        };
      })
      .filter(g => g.columns.length > 0);
  }, [effectiveInputTables, accessibleDatabases]);

  // All secondary tables (related + free) from persisted config
  const secondaryTables = useMemo(
    () => (config.inputTables ?? []).filter(t => t.role === 'secondary'),
    [config.inputTables]
  );

  // sl-m379: IDs already used (lead + all secondaries) — for exclude in picker
  const usedTableIds = useMemo(() => {
    const ids = secondaryTables.map(t => t.tableId).filter(Boolean);
    if (config.sourceTableId) ids.push(config.sourceTableId);
    return ids;
  }, [secondaryTables, config.sourceTableId]);

  // sl-m379: Add a new empty secondary table row
  const handleAddSecondaryTable = useCallback(() => {
    const current = config.inputTables ?? [];
    // Don't add if there's already an empty (unselected) secondary
    if (current.some(t => t.role === 'secondary' && !t.tableId)) return;
    onUpdate({
      inputTables: [
        ...current,
        {
          id: Math.random().toString(36).slice(2, 10),
          tableId: '',
          tableName: '',
          role: 'secondary' as const,
        },
      ],
    });
  }, [config.inputTables, onUpdate]);

  // sl-m379: Update a secondary table selection
  const handleUpdateSecondaryTable = useCallback(
    (index: number, database: DatabaseInfo) => {
      const manualOnly = (config.inputTables ?? []).filter(
        t => t.role === 'secondary' && !t.linkedVia
      );
      const updated = [...manualOnly];
      updated[index] = {
        ...updated[index],
        tableId: database.blockId,
        tableName: database.databaseTitle,
        docId: database.docId,
      };
      // Preserve related tables (linked via relation columns)
      const relatedTables = (config.inputTables ?? []).filter(
        t => t.role === 'secondary' && t.linkedVia
      );
      onUpdate({ inputTables: [...relatedTables, ...updated] });
    },
    [config.inputTables, onUpdate]
  );

  // sl-m379: Remove a manual secondary table
  const handleRemoveSecondaryTable = useCallback(
    (id: string) => {
      const current = config.inputTables ?? [];
      const removed = current.find(t => t.id === id);
      const updated = current.filter(t => t.id !== id);
      // Clean up orphaned mappings
      const cleanedMappings = removed?.tableId
        ? (config.inputMappings ?? []).filter(
            // Remove both new-format mappings (InputTable.id) and legacy ones (tableId)
            m =>
              m.inputTableId !== removed.id &&
              m.inputTableId !== removed.tableId
          )
        : config.inputMappings;
      onUpdate({ inputTables: updated, inputMappings: cleanedMappings });
    },
    [config.inputTables, config.inputMappings, onUpdate]
  );

  // Batch size handler (T047)
  // Batch mode: when enabled, one runner call processes multiple normal rows.
  const configuredBatchSize = config.batchConfig?.batchSize ?? 1;
  const executionMode = config.batchConfig?.executionMode ?? 'row';
  const rowsPerCall = clampRowsPerCall(
    executionMode === 'batch' ? configuredBatchSize : 1,
    executionMode === 'batch' ? ROWS_PER_CALL_DEFAULT : ROWS_PER_CALL_MIN
  );

  const updateRowsPerCall = useCallback(
    (size: number) => {
      const nextSize = clampRowsPerCall(size, ROWS_PER_CALL_MIN);
      const nextMode = nextSize > 1 ? 'batch' : 'row';

      onUpdate({
        batchConfig: {
          ...config.batchConfig,
          batchSize: nextSize,
          executionMode: nextMode,
          retry: config.batchConfig?.retry ?? { ...DEFAULT_BATCH_RETRY },
          onFailure: config.batchConfig?.onFailure ?? 'stop',
        },
      });
    },
    [config.batchConfig, onUpdate]
  );

  const handleRowsPerCallChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      updateRowsPerCall(Number(e.currentTarget.value));
    },
    [updateRowsPerCall]
  );

  return (
    <div className={styles.tabContent}>
      {/* ── Trigger ── */}
      <div data-section="trigger">
        <CollapsibleSection
          title={t['com.streamlain.streamlain.process.trigger']()}
          subtitle={t['com.streamlain.streamlain.process.trigger-subtitle']()}
          onInfoClick={() => registry.start('automation:processSchedule')}
        >
          {/* Trigger Type */}
          <SectionControlRow
            label={t['com.streamlain.streamlain.process.trigger-type']()}
            coachmarkTarget="trigger-type-menu"
          >
            <Menu
              items={
                <>
                  {triggerTypeOptions.map(option => (
                    <MenuItem
                      key={option.value}
                      onClick={() => handleTriggerTypeChange(option.value)}
                      selected={triggerConfig.type === option.value}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </>
              }
            >
              <button
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 10px',
                  fontSize: '12px',
                  border: '1px solid var(--streamlain-border-color)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--streamlain-background-primary-color)',
                  color: 'var(--streamlain-text-primary-color)',
                  cursor: 'pointer',
                }}
              >
                {triggerTypeOptions.find(o => o.value === triggerConfig.type)
                  ?.label ||
                  t['com.streamlain.streamlain.process.trigger-manual']()}
                <span
                  style={{ color: cssVarV2('text/tertiary'), fontSize: '10px' }}
                >
                  ▼
                </span>
              </button>
            </Menu>
          </SectionControlRow>

          {/* Scheduled Trigger Config */}
          {triggerConfig.type === 'scheduled' && (
            <>
              <SectionControlRow
                label={t['com.streamlain.streamlain.process.schedule']()}
              >
                <Menu
                  items={
                    <>
                      {Object.keys(CRON_PRESETS).map(key => (
                        <MenuItem
                          key={key}
                          onClick={() =>
                            !isScheduleSaving &&
                            handleCronPresetChange(key as CronPreset)
                          }
                          selected={
                            (triggerConfig.cronPreset || 'hourly') === key
                          }
                          disabled={isScheduleSaving}
                        >
                          {cronI18n[key] ?? key}
                        </MenuItem>
                      ))}
                    </>
                  }
                >
                  <button
                    disabled={isScheduleSaving}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 10px',
                      fontSize: '12px',
                      border: '1px solid var(--streamlain-border-color)',
                      borderRadius: '6px',
                      backgroundColor:
                        'var(--streamlain-background-primary-color)',
                      color: 'var(--streamlain-text-primary-color)',
                      cursor: isScheduleSaving ? 'not-allowed' : 'pointer',
                      opacity: isScheduleSaving ? 0.6 : 1,
                    }}
                  >
                    {cronI18n[triggerConfig.cronPreset || 'hourly'] ||
                      t['com.streamlain.streamlain.process.cron.hourly']()}
                    <span
                      style={{
                        color: cssVarV2('text/tertiary'),
                        fontSize: '10px',
                      }}
                    >
                      ▼
                    </span>
                  </button>
                </Menu>
              </SectionControlRow>

              {/* Enable Toggle */}
              <SectionControlRow
                label={
                  isScheduleSaving
                    ? t['com.streamlain.streamlain.common.saving']()
                    : t['com.streamlain.streamlain.process.enable-schedule']()
                }
                coachmarkTarget="trigger-enable-toggle"
              >
                <button
                  onClick={() => void handleTriggerEnabledToggle()}
                  disabled={
                    isScheduleSaving ||
                    (!triggerConfig.enabled && missingAiKeys.length > 0)
                  }
                  style={{
                    width: '44px',
                    height: '24px',
                    borderRadius: '12px',
                    border: 'none',
                    cursor:
                      isScheduleSaving || missingAiKeys.length > 0
                        ? 'not-allowed'
                        : 'pointer',
                    backgroundColor: triggerConfig.enabled
                      ? 'var(--streamlain-primary-color)'
                      : 'var(--streamlain-border-color)',
                    position: 'relative',
                    transition: 'background-color 0.2s',
                    opacity:
                      isScheduleSaving ||
                      (!triggerConfig.enabled && missingAiKeys.length > 0)
                        ? 0.6
                        : 1,
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: '2px',
                      left: triggerConfig.enabled ? '22px' : '2px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      transition: 'left 0.2s',
                    }}
                  />
                </button>
              </SectionControlRow>
            </>
          )}
        </CollapsibleSection>
      </div>

      {/* sl-m379: Input Tables — CollapsibleSection */}
      <div data-section="input-tables">
        <CollapsibleSection
          title={t['com.streamlain.streamlain.input.source']()}
          subtitle={
            t['com.streamlain.streamlain.input.source-subtitle']?.() ??
            'Sources and relations'
          }
          onInfoClick={() =>
            setTimeout(() => registry.start('automation:inputSource'), 50)
          }
          defaultCollapsed
        >
          {/* === Lead-Cluster: Lead Table + Related Tables === */}
          {(() => {
            const lead = effectiveInputTables.find(t => t.role === 'lead');
            const relatedTables = effectiveInputTables.filter(t => t.linkedVia);
            const freeTables = effectiveInputTables.filter(
              t => t.role === 'secondary' && !t.linkedVia
            );
            const freeManualOnly = (config.inputTables ?? []).filter(
              t => t.role === 'secondary' && !t.linkedVia
            );

            return (
              <>
                {/* Lead Table */}
                {lead && (
                  <SectionTableRow
                    action={
                      lead.tableId || hasSelectedWebhookSource ? (
                        <SectionRemoveButton
                          onClick={() => {
                            onUpdate({
                              sourceTableId: null,
                              sourceTableName: null,
                              sourceDocId: null,
                              selectedColumns: [],
                              filterGroup: undefined,
                              inputMappings: [],
                              inputTables: [],
                              statusTracking: {
                                ...statusTracking,
                                enabled: false,
                                columnId: null,
                              },
                            });
                            handleTriggerConfigChange({
                              webhookCredentialId: null,
                            });
                          }}
                          title={t[
                            'com.streamlain.streamlain.input.clear-source'
                          ]()}
                        />
                      ) : null
                    }
                  >
                    <TableSelector
                      value={lead.tableId || undefined}
                      onChange={handleSourceTableChange}
                      placeholder={t[
                        'com.streamlain.streamlain.input.select-source-table'
                      ]()}
                      webhookOptions={
                        triggerConfig.type === 'webhook'
                          ? workspaceWebhooks
                          : undefined
                      }
                      selectedWebhookId={
                        triggerConfig.type === 'webhook'
                          ? (triggerConfig.webhookCredentialId ?? null)
                          : undefined
                      }
                      onWebhookSelect={handleWebhookCredentialSelect}
                    />
                  </SectionTableRow>
                )}

                {/* Related Tables (tight to lead, indented, badge inside row) */}
                {relatedTables.map(table => (
                  <SectionTableRow
                    key={table.id}
                    indented
                    action={
                      <SectionRemoveButton
                        onClick={() => handleRemoveSecondaryTable(table.id)}
                        title={t[
                          'com.streamlain.streamlain.common.remove-table'
                        ]()}
                      />
                    }
                  >
                    <>
                      <div style={{ flex: 1 }}>
                        <TableSelector
                          value={table.tableId || undefined}
                          onChange={() => {}}
                          placeholder={t[
                            'com.streamlain.streamlain.input.select-source-table'
                          ]()}
                          excludeBlockIds={usedTableIds}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: '10px',
                          fontWeight: 700,
                          padding: '1px 5px',
                          borderRadius: '3px',
                          backgroundColor:
                            table.cardinality === 'many'
                              ? '#8b5cf6'
                              : '#3b82f6',
                          color: '#fff',
                          lineHeight: '14px',
                          flexShrink: 0,
                        }}
                        title={
                          table.cardinality === 'many'
                            ? t[
                                'com.streamlain.streamlain.input.relation-many'
                              ]()
                            : t[
                                'com.streamlain.streamlain.input.auto-detected-relation'
                              ]()
                        }
                      >
                        {table.cardinality === 'many' ? 'N' : '1'}
                      </span>
                    </>
                  </SectionTableRow>
                ))}

                {/* "+ Add Related Table" — always visible when lead is set */}
                {config.sourceTableId && (
                  <div style={{ paddingLeft: '12px', marginTop: '2px' }}>
                    {availableRelations.length > 0 ? (
                      <Menu
                        items={
                          <>
                            {availableRelations.map(rel => (
                              <MenuItem
                                key={rel.columnId}
                                onClick={() => handleAddRelatedTable(rel)}
                              >
                                <span
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    minWidth: 0,
                                  }}
                                >
                                  <span
                                    style={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      flexShrink: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    {rel.columnName}
                                  </span>
                                  <span style={{ opacity: 0.5, flexShrink: 0 }}>
                                    →
                                  </span>
                                  <span
                                    style={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      flexShrink: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    {rel.targetTableName}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: '10px',
                                      fontWeight: 700,
                                      padding: '1px 4px',
                                      borderRadius: '3px',
                                      backgroundColor:
                                        rel.cardinality === 'many'
                                          ? '#8b5cf6'
                                          : '#3b82f6',
                                      color: '#fff',
                                      lineHeight: '14px',
                                      flexShrink: 0,
                                    }}
                                  >
                                    {rel.cardinality === 'many' ? 'N' : '1'}
                                  </span>
                                </span>
                              </MenuItem>
                            ))}
                          </>
                        }
                      >
                        <SectionAddLink>
                          <span>+</span>
                          {t[
                            'com.streamlain.streamlain.input.add-related-table'
                          ]()}
                        </SectionAddLink>
                      </Menu>
                    ) : (
                      <SectionAddLink disabled>
                        <span>+</span>
                        {t[
                          'com.streamlain.streamlain.input.add-related-table'
                        ]()}
                      </SectionAddLink>
                    )}
                  </div>
                )}

                {/* === Separator (indented, tight spacing equal above/below) === */}
                {config.sourceTableId && <SectionDivider />}

                {/* === Free Tables === */}
                {freeTables.map(table => {
                  const manualIndex = freeManualOnly.findIndex(
                    ft => ft.id === table.id
                  );
                  return (
                    <SectionTableRow
                      key={table.id}
                      spaced
                      action={
                        <SectionRemoveButton
                          onClick={() => handleRemoveSecondaryTable(table.id)}
                          title={t[
                            'com.streamlain.streamlain.common.remove-table'
                          ]()}
                        />
                      }
                    >
                      <TableSelector
                        value={table.tableId || undefined}
                        onChange={db =>
                          handleUpdateSecondaryTable(manualIndex, db)
                        }
                        placeholder={t[
                          'com.streamlain.streamlain.input.select-source-table'
                        ]()}
                        excludeBlockIds={usedTableIds}
                      />
                    </SectionTableRow>
                  );
                })}

                {/* "+ Add Table" for free tables */}
                <SectionAddLink onClick={handleAddSecondaryTable}>
                  <span>+</span>
                  {t['com.streamlain.streamlain.common.add-table']()}
                </SectionAddLink>
              </>
            );
          })()}
        </CollapsibleSection>
      </div>
      {/* 1. Fields Section (Primary) - Column Selection / Input Mapping */}
      <div data-section="fields">
        {/* sl-za0v.13.2: Schema loading → Skeleton, Error → Banner, Success → Grid, No workflow → ColumnSelector */}
        {isSchemaLoading ? (
          <CollapsibleSection
            title={t['com.streamlain.streamlain.input.input-mapping']()}
            subtitle={t[
              'com.streamlain.streamlain.input.input-mapping-subtitle'
            ]()}
            defaultCollapsed
          >
            <SectionStack>
              {[1, 2, 3].map(i => (
                <div
                  key={i}
                  style={{
                    height: '32px',
                    borderRadius: '4px',
                    backgroundColor: cssVarV2('layer/background/hoverOverlay'),
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                />
              ))}
            </SectionStack>
          </CollapsibleSection>
        ) : schemaLoadFailed ? (
          <CollapsibleSection
            title={t['com.streamlain.streamlain.input.input-mapping']()}
            defaultCollapsed
          >
            <SectionNotice variant="warning">
              {t['com.streamlain.streamlain.input.schema-load-failed']?.() ??
                'Schema could not be loaded. Define input/output fields in the workflow Start/End nodes.'}
            </SectionNotice>
          </CollapsibleSection>
        ) : hasSchema && selectedSchema ? (
          <CollapsibleSection
            title={t['com.streamlain.streamlain.input.input-mapping']()}
            subtitle={t[
              'com.streamlain.streamlain.input.input-mapping-subtitle'
            ]()}
            onInfoClick={() => registry.start('automation:inputFields')}
            defaultCollapsed
          >
            <div data-coachmark-target="input-mapping-grid">
              <InputMappingGrid
                schemaFields={selectedSchema.input}
                sourceColumns={sourceColumns ?? []}
                mappings={config.inputMappings ?? []}
                onMappingsChange={handleInputMappingsChange}
                secondaryColumnGroups={secondaryColumnGroups}
                sourceTableName={config.sourceTableName ?? undefined}
                webhookGroup={
                  triggerConfig.type === 'webhook'
                    ? {
                        name:
                          workspaceWebhooks.find(
                            w =>
                              w.credentialId ===
                              triggerConfig.webhookCredentialId
                          )?.name ??
                          t[
                            'com.streamlain.streamlain.input.webhook-trigger'
                          ]?.() ??
                          'Webhook',
                      }
                    : undefined
                }
              />
            </div>
          </CollapsibleSection>
        ) : (
          <ColumnSelector
            sourceTableId={config.sourceTableId}
            selectedColumns={config.selectedColumns}
            onChange={handleColumnSelectionChange}
            onInfoClick={() => registry.start('automation:inputFields')}
            secondaryColumnGroups={secondaryColumnGroups}
          />
        )}
      </div>

      {/* 2. Filters Section — one section, per-table filter bars inside */}
      <div data-section="filters">
        {secondaryTables.length === 0 ? (
          // Single table: standard NativeFilterPanel with wrapper
          <NativeFilterPanel
            sourceTableId={config.sourceTableId}
            filterGroup={config.filterGroup}
            onChange={handleFilterChange}
            onInfoClick={() => registry.start('automation:inputFilter')}
            systemFilter={
              hasSystemFilter && systemFilterColumnName
                ? {
                    columnName: systemFilterColumnName,
                    filterValue: statusTracking.successValue,
                  }
                : null
            }
          />
        ) : (
          // Multi-table: one CollapsibleSection, multiple noWrapper filter bars
          <CollapsibleSection
            title={t['com.streamlain.streamlain.input.filters']()}
            subtitle={t['com.streamlain.streamlain.input.filters-subtitle']()}
            onInfoClick={() => registry.start('automation:inputFilter')}
            defaultCollapsed
          >
            {/* Lead table filter */}
            <NativeFilterPanel
              sourceTableId={config.sourceTableId}
              filterGroup={config.filterGroup}
              onChange={handleFilterChange}
              systemFilter={
                hasSystemFilter && systemFilterColumnName
                  ? {
                      columnName: systemFilterColumnName,
                      filterValue: statusTracking.successValue,
                    }
                  : null
              }
              label={config.sourceTableName ?? 'Source'}
              noWrapper
            />
            {/* Secondary table filters */}
            {secondaryTables.map(table =>
              table.tableId ? (
                <NativeFilterPanel
                  key={table.id}
                  sourceTableId={table.tableId}
                  filterGroup={table.filterGroup as FilterGroup | undefined}
                  onChange={fg =>
                    handleSecondaryFilterChange(table.tableId, fg)
                  }
                  label={table.tableName}
                  noWrapper
                />
              ) : null
            )}
          </CollapsibleSection>
        )}
      </div>

      {/* 3. Status Tracking — controls which rows are skipped */}
      <div data-section="status-tracking">
        <StatusTrackingSection
          config={config}
          onUpdate={onUpdate}
          selectColumns={primarySourceColumns.filter(
            col => col.type === 'select'
          )}
          usedColumns={usedColumns}
          onNavigateToConflict={onNavigateToConflict}
          onCreateStatusColumn={onCreateStatusColumn}
          onInfoClick={() => registry.start('automation:statusTracking')}
        />
      </div>

      {/* 4. Advanced Section - Rows per call */}
      <div data-section="advanced">
        <CollapsibleSection
          title={t['com.streamlain.streamlain.common.advanced']()}
          subtitle={t['com.streamlain.streamlain.input.advanced-subtitle']()}
          onInfoClick={() => registry.start('automation:inputBatch')}
          defaultCollapsed
        >
          <div className={styles.rowsPerCallContent} data-section="batch-size">
            <SectionPanelHeader
              title={t['com.streamlain.streamlain.input.rows-per-call']()}
              description={t[
                'com.streamlain.streamlain.input.rows-per-call-help'
              ]()}
              action={
                <label
                  className={styles.rowsPerCallInputWrapper}
                  data-coachmark-target="batch-value"
                >
                  <input
                    type="number"
                    min={ROWS_PER_CALL_MIN}
                    max={ROWS_PER_CALL_MAX}
                    step="1"
                    value={rowsPerCall}
                    onChange={handleRowsPerCallChange}
                    className={styles.rowsPerCallNumberInput}
                    aria-label={t[
                      'com.streamlain.streamlain.input.rows-per-call'
                    ]()}
                  />
                  <span className={styles.rowsPerCallUnit}>
                    {t['com.streamlain.streamlain.input.rows-per-call-unit']()}
                  </span>
                </label>
              }
            />
            <div className={styles.sliderRow}>
              <input
                type="range"
                min={ROWS_PER_CALL_MIN}
                max={ROWS_PER_CALL_MAX}
                value={rowsPerCall}
                onChange={handleRowsPerCallChange}
                className={styles.sliderInput}
              />
            </div>
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
};
